//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import DateString from '@models/DateString';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import TimeZoneId from '@models/TimeZoneId';
import { ICalendarEvent } from '@universal-scheduling/components/calendar/ICalendarEvent';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { getViewDates } from '@universal-scheduling/redux/calendar/universalCalendarUtils';
import { momentFromDate } from '@utils/moment';

/**
 * Gets the text to display in the tooltip when dragging an item to next and last week
 */
export const getNextAndPreviousWeeksText = (
  startOfWeekDay: number,
  startDate: DateString,
) => {
  const { startDate: newStartDatePrevious, endDate: newEndDatePrevious } =
    getViewDates(startDate, startOfWeekDay, UniversalCalendarView.Week, -1);
  const { startDate: newStartDateNext, endDate: newEndDateNext } = getViewDates(
    startDate,
    startOfWeekDay,
    UniversalCalendarView.Week,
    1,
  );

  const includesMonthChange = (start: DateString, end: DateString) =>
    DateString.parse(start).month !== DateString.parse(end).month;
  const includesYearChange = (start: DateString, end: DateString) =>
    DateString.parse(start).year !== DateString.parse(end).year;

  const getFormat = (
    isFirst: boolean,
    nextStartDate: DateString,
    previousStartDate: DateString,
  ) => {
    if (includesYearChange(nextStartDate, previousStartDate)) {
      return 'MMM D, YYYY';
    }

    if (includesMonthChange(nextStartDate, previousStartDate) || isFirst) {
      return 'MMM D';
    }

    return 'D';
  };

  return {
    next: `${DateString.format(
      newStartDateNext,
      getFormat(true, newStartDateNext, newEndDateNext),
    )} - ${DateString.format(
      newEndDateNext,
      getFormat(false, newStartDateNext, newEndDateNext),
    )}`,
    previous: `${DateString.format(
      newStartDatePrevious,
      getFormat(true, newStartDatePrevious, newEndDatePrevious),
    )} - ${DateString.format(
      newEndDatePrevious,
      getFormat(false, newStartDatePrevious, newEndDatePrevious),
    )}`,
  };
};

/**
 * Gets the endDate at the end of the current week
 */
export const getWeekViewEndDate = (
  startDate: DateString,
  startOfWeek: number,
): DateString => {
  return DateString.applyMomentOperation(
    getWeekViewStartDate(startDate, startOfWeek),
    'add',
    6,
    'd',
  );
};

/**
 * Calculates the startDate for when we navigate forwards in time by a week.
 */
export const getNextWeekStartDate = (
  startDate: DateString,
  startOfWeek: number,
) => {
  const startOfNextWeek = DateString.applyMomentOperation(
    startDate,
    'add',
    1,
    'w',
  );
  return getWeekViewStartDate(startOfNextWeek, startOfWeek);
};

/**
 * Calculates the startDate for when we navigate backwards in time by a week
 */
export const getPreviousWeekStartDate = (
  startDate: DateString,
  startOfWeek: number,
) => {
  const startOfPreviousWeek = DateString.applyMomentOperation(
    startDate,
    'subtract',
    1,
    'w',
  );
  return getWeekViewStartDate(startOfPreviousWeek, startOfWeek);
};

/**
 * Calculates the startDate for the week view given a particular date by determining
 * the startOfWeek day prior to the given date.
 */
export const getWeekViewStartDate = (
  dateString: DateString,
  startOfWeek: number,
) => {
  const date = DateString.toMoment(dateString, TimeZoneId.Utc);
  const firstDaysToSubtract = (7 + date.weekday() - startOfWeek) % 7;
  return DateString.applyMomentOperation(
    dateString,
    'subtract',
    firstDaysToSubtract,
    'd',
  );
};

export const sortWeekViewEventsByTime = (
  events: ICalendarEvent[],
  timezone: TimeZoneId,
) => [...events].sort(weekViewEventsByTimeSorter(timezone));

export const weekViewEventsByTimeSorter =
  (timezone: TimeZoneId) => (a: ICalendarEvent, b: ICalendarEvent) => {
    const aMoment = momentFromDate(a.date, timezone);
    const bMoment = momentFromDate(b.date, timezone);
    if (aMoment.get('hours') < bMoment.get('hours')) {
      return -1;
    } else if (aMoment.get('hours') === bMoment.get('hours')) {
      return aMoment.get('minutes') < bMoment.get('minutes') ? -1 : 1;
    }
    return 1;
  };

export const sortWeekViewEvents =
  (timezone: TimeZoneId) => (a: ICalendarEvent, b: ICalendarEvent) =>
    momentFromDate(a.date, timezone).isBefore(momentFromDate(b.date, timezone))
      ? -1
      : 1;

export const getGridColRow = (idx: number, mediaCount: number) => {
  const mediaNum = idx + 1;
  const colCount = 2;
  const rowCount = 2;
  const colNum = mediaNum <= colCount ? mediaNum : mediaNum - colCount;

  switch (mediaCount) {
    case 1:
      return {
        col: `1 / span ${colCount}`,
        row: `1 / span ${rowCount}`,
      };
    case 2:
      return {
        col: `${colNum}`,
        row: `1 / span ${rowCount}`,
      };
    case 3:
      return {
        col: `${colNum}`,
        row: `${mediaNum === 1 ? mediaNum : mediaNum - 1}`,
      };
    case 4:
      return {
        col: `${colNum}`,
        row: `${mediaNum <= colCount ? 1 : colCount}`,
      };
    default: {
      if (mediaNum > colCount * rowCount) {
        return {
          col: `${colCount}`,
          row: `${rowCount}`,
        };
      }
      return {
        col: `${colNum}`,
        row: `${mediaNum <= colCount ? 1 : colCount}`,
      };
    }
  }
};
